import type React from 'react';
import type { ChangeEvent, KeyboardEventHandler } from 'react';
import { useEffect, useState } from 'react';

import { isStringInvalid } from '@polyai/common/lib/miscHelper';

import IconButton from 'components/atoms/IconButton';
import { CloseCircleIcon } from 'components/atoms/Icons/CloseCircleIcon';
import { LegacyTag, LegacyTagVariant } from 'components/atoms/LegacyTag';
import type { OptionType } from 'components/atoms/Select';
import * as Styled from './TagInput.styled';

export type Delimiter = 'comma' | 'semicolon' | 'space' | 'tab' | 'newline';

const DELIMITER_REGEX_MAP: Record<Delimiter, string> = {
  comma: ',',
  semicolon: ';',
  space: '\\s',
  tab: '\\t',
  newline: '\\n',
};

const buildDelimiterRegex = (delimiters: Delimiter[]) => {
  const pattern = delimiters.map(d => DELIMITER_REGEX_MAP[d]).join('');
  return new RegExp(`[${pattern}]+`);
};

type TagInputProps = {
  tags: string[];
  onAdd?: (tags: string[]) => void;
  onDelete?: (tags: string[]) => void;
  onBlur?: (value: string[]) => void;
  placeholder?: string;
  keyword?: boolean;
  error?: string;
  disabled?: boolean;
  readonly?: boolean;
  options?: OptionType[];
  delimiters?: Delimiter[];
};

const TagInput: React.FC<TagInputProps> = ({
  tags,
  onAdd,
  onDelete,
  onBlur,
  placeholder,
  keyword,
  error,
  disabled = false,
  readonly = false,
  options = [],
  delimiters = ['comma'],
}) => {
  const delimiterRegex = buildDelimiterRegex(delimiters);
  const [tagInput, setTagInput] = useState('');
  const [activeTags, setActiveTags] = useState(tags);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<OptionType[]>([]);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);

  const filterOptions = (input: string) => {
    if (!options.length || !input.trim() || !activeTags) {
      return [];
    }

    return options.filter(
      option =>
        option.label.toLowerCase().includes(input.toLowerCase()) &&
        !activeTags.includes(option.value),
    );
  };

  const handleSelectOption = (option: OptionType) => {
    const updatedTags = activeTags
      ? [...activeTags, option.value]
      : [option.value];
    setActiveTags([...updatedTags]);
    setTagInput('');
    setShowDropdown(false);
    setSelectedOptionIndex(-1);

    if (onAdd) {
      onAdd(updatedTags);
    }
  };

  const handleAddTag = () => {
    const tag = tagInput;
    if (activeTags?.includes(tag) || isStringInvalid(tag)) {
      return activeTags;
    }
    const updatedTags = activeTags ? [...activeTags, tag] : [tag];
    setActiveTags(updatedTags);
    setTagInput('');
    setShowDropdown(false);
    setSelectedOptionIndex(-1);

    if (onAdd) {
      onAdd(updatedTags);
    }

    return updatedTags;
  };

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
  const handleKeydownEvent: KeyboardEventHandler<HTMLInputElement> = e => {
    if (showDropdown && filteredOptions.length > 0) {
      if (e.code === 'ArrowDown') {
        e.preventDefault();
        setSelectedOptionIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0,
        );
      } else if (e.code === 'ArrowUp') {
        e.preventDefault();
        setSelectedOptionIndex(prev =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1,
        );
      } else if (e.code === 'Enter') {
        e.preventDefault();
        if (
          selectedOptionIndex >= 0 &&
          selectedOptionIndex < filteredOptions.length
        ) {
          handleSelectOption(filteredOptions[selectedOptionIndex]);
        } else {
          handleAddTag();
        }
        return;
      } else if (e.code === 'Escape') {
        e.preventDefault();
        setShowDropdown(false);
        setSelectedOptionIndex(-1);
        return;
      }
    } else if (e.code === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleDeleteTag = (tag: string) => {
    const updatedTags = activeTags?.filter(t => t !== tag);
    setActiveTags(() => [...updatedTags]);

    if (onDelete) {
      onDelete(updatedTags);
    }
  };

  const splitAndAddTags = (text: string) => {
    const splitText = text
      .split(delimiterRegex)
      .map(item => item.trim())
      .filter(item => item.length > 0 && !activeTags?.includes(item));

    if (!splitText.length) {
      setTagInput('');
      return;
    }

    const updatedTags = [...activeTags, ...splitText];
    setActiveTags(updatedTags);
    setTagInput('');

    if (onAdd) {
      onAdd(updatedTags);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData('text');
    if (delimiterRegex.test(pastedText)) {
      e.preventDefault();
      splitAndAddTags(pastedText);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;

    // Handle delimiter-separated input
    if (delimiterRegex.test(text)) {
      splitAndAddTags(text);
      return;
    }

    setTagInput(text);

    // Filter options and show dropdown if there are matches
    if (options.length > 0) {
      const filtered = filterOptions(text);
      setFilteredOptions(filtered);
      setShowDropdown(filtered.length > 0);
      setSelectedOptionIndex(-1);
    } else {
      setShowDropdown(false);
    }
  };

  const handleBlurEvent = () => {
    // Delay hiding dropdown to allow for option clicks
    setTimeout(() => {
      setShowDropdown(false);
      setSelectedOptionIndex(-1);
    }, 150);

    const updatedTags = handleAddTag();
    !!onBlur && onBlur(updatedTags);
  };

  useEffect(() => {
    setActiveTags(tags);
  }, [tags]);

  return (
    <>
      <Styled.DropdownWrapper>
        <Styled.TagInputWrapper $isError={!!error}>
          {activeTags?.map(tag => {
            const tagOption = options.find(opt => opt.value === tag);
            const isTagDisabled = tagOption?.isDisabled || false;

            return (
              <LegacyTag
                key={`${tag}-tag`}
                append={
                  !readonly && (
                    <IconButton
                      disabled={disabled || isTagDisabled}
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDeleteTag(tag);
                      }}
                    >
                      <CloseCircleIcon
                        color={keyword ? 'iconPrimaryInverse' : 'iconPrimary'}
                        data-test-id="tag-delete-icon"
                      />
                    </IconButton>
                  )
                }
                data-test-id="tag"
                variant={
                  keyword ? LegacyTagVariant.KEYWORD : LegacyTagVariant.DEFAULT
                }
              >
                {tag}
              </LegacyTag>
            );
          })}
          <Styled.TagInput
            data-test-id="tag-input"
            disabled={disabled}
            name="tagInput"
            placeholder={placeholder}
            readOnly={readonly}
            type="text"
            value={tagInput}
            data-1p-ignore={true}
            onBlur={handleBlurEvent}
            onChange={handleInputChange}
            onKeyDown={handleKeydownEvent}
            onPaste={handlePaste}
          />
        </Styled.TagInputWrapper>
        {showDropdown && filteredOptions.length > 0 && (
          <Styled.Dropdown data-test-id="tag-input-dropdown">
            {filteredOptions.map((option, index) => (
              <Styled.DropdownOption
                key={option.value}
                $isSelected={index === selectedOptionIndex}
                data-test-id={`tag-option-${option.value}`}
                onMouseDown={() => handleSelectOption(option)}
                onMouseEnter={() => setSelectedOptionIndex(index)}
              >
                {option.label}
              </Styled.DropdownOption>
            ))}
          </Styled.Dropdown>
        )}
      </Styled.DropdownWrapper>
      <Styled.ErrorMessage>{error}</Styled.ErrorMessage>
    </>
  );
};

export default TagInput;
